import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, TrendingUp, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { DateCell } from "@/components/DateCell";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface CustomerForecast {
  id: string;
  accountId: string | null;
  source: string;
  confidenceLevel: string | null;
  forecastPeriodStart: string;
  forecastPeriodEnd: string;
  receivedAt: string;
  notes: string | null;
  account?: { id: string; name: string };
}

interface Account {
  id: string;
  name: string;
}

const CONFIDENCE_COLORS: Record<string, string> = {
  COMMITTED: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  PLANNED: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  TENTATIVE: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
};

const SOURCE_OPTIONS = ["MANUAL", "EMAIL", "EDI", "API", "IMPORT"];
const CONFIDENCE_OPTIONS = ["COMMITTED", "PLANNED", "TENTATIVE"];

export default function ForecastsPage() {
  const { sessionId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({
    accountId: "__none__",
    source: "MANUAL",
    confidenceLevel: "PLANNED",
    forecastPeriodStart: "",
    forecastPeriodEnd: "",
    notes: "",
  });

  const { data, isLoading } = useQuery<{ forecasts: CustomerForecast[]; total: number }>({
    queryKey: ["/api/forecasts", { search }],
    queryFn: () =>
      api.get<{ forecasts: CustomerForecast[]; total: number }>(
        `/forecasts?search=${encodeURIComponent(search)}`,
        sessionId
      ),
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
    queryFn: () => api.get<any>(`/accounts`, sessionId).then((r) => r.accounts || r),
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        accountId: form.accountId === "__none__" ? null : form.accountId,
      };
      return api.post(`/forecasts`, body, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forecasts"] });
      setDrawerOpen(false);
      setForm({
        accountId: "__none__", source: "MANUAL", confidenceLevel: "PLANNED",
        forecastPeriodStart: "", forecastPeriodEnd: "", notes: "",
      });
      toast({ title: "Forecast created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const forecasts = data?.forecasts || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Forecasts"
        description="Demand signals from customers for inventory planning"
        actions={
          <Button onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Forecast
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search forecasts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading forecasts...</div>
          ) : forecasts.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">No customer forecasts</p>
              <p className="text-sm text-muted-foreground mt-1">
                Track demand signals from customers to plan inventory and purchasing.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Entry</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Account</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Confidence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Period Start</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Period End</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {forecasts.map((f) => (
                    <tr key={f.id} className="hover:bg-muted/40">
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-muted-foreground">
                        <DateCell value={f.receivedAt} />
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span>{f.account?.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <Badge variant="outline">{f.source}</Badge>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {f.confidenceLevel ? (
                          <Badge variant="outline" className={CONFIDENCE_COLORS[f.confidenceLevel] || ""}>
                            {f.confidenceLevel}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(f.forecastPeriodStart).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(f.forecastPeriodEnd).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {f.notes || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="New Customer Forecast"
        description="Record a demand signal from a customer for inventory planning."
        formId="create-forecast"
        isPending={createMutation.isPending}
        submitLabel="Create Forecast"
        submitDisabled={!form.forecastPeriodStart || !form.forecastPeriodEnd}
        size="md"
        data-testid="drawer-new-forecast"
      >
        <form
          id="create-forecast"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="text-sm font-medium mb-1 block">Account</label>
            <Select
              value={form.accountId}
              onValueChange={(v) => setForm({ ...form, accountId: v })}
            >
              <SelectTrigger variant="flat"><SelectValue placeholder="Select account (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No account</SelectItem>
                {accounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Source</label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger variant="flat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Confidence</label>
              <Select
                value={form.confidenceLevel}
                onValueChange={(v) => setForm({ ...form, confidenceLevel: v })}
              >
                <SelectTrigger variant="flat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONFIDENCE_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Period Start *</label>
              <Input
                variant="flat"
                type="date"
                value={form.forecastPeriodStart}
                onChange={(e) => setForm({ ...form, forecastPeriodStart: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Period End *</label>
              <Input
                variant="flat"
                type="date"
                value={form.forecastPeriodEnd}
                onChange={(e) => setForm({ ...form, forecastPeriodEnd: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Notes</label>
            <Textarea
              variant="flat"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>
        </form>
      </FormDrawer>
    </div>
  );
}
